import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Check, Loader2 } from "lucide-react";
import axios from "axios";

const API = "/api";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      setStatus("success");
      setMessage(res.data.message);
      setEmail("");
      setTimeout(() => setStatus("idle"), 4000);
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ backgroundColor: "#262626" }}
    >
      {/* Noise */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }}
      />

      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
            Mantente Conectado
          </p>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#F5F5F0] tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Únete a la <span className="gold-text">Familia</span>
          </h2>
          <p className="text-base text-[#A3A3A3] mb-10 max-w-md mx-auto leading-relaxed">
            Sé el primero en enterarte de nuevos sabores, lanzamientos exclusivos y recetas. Sin spam, puro sabor.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row items-stretch gap-0 max-w-lg mx-auto"
          data-testid="newsletter-form"
        >
          <input
            type="email"
            placeholder="tucorreo@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="newsletter-email-input"
            className="flex-1 bg-transparent border border-white/10 border-r-0 sm:border-r-0 text-[#F5F5F0] px-6 py-4 text-sm placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none transition-colors"
            style={{ fontFamily: "'Inter', sans-serif" }}
          />
          <button
            type="submit"
            disabled={status === "loading" || status === "success"}
            data-testid="newsletter-submit-btn"
            className="bg-[#D4AF37] text-[#1A1A1A] px-8 py-4 font-semibold uppercase tracking-wider text-sm hover:bg-[#E5C158] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 gold-glow min-w-[140px]"
          >
            {status === "loading" && <Loader2 size={16} className="animate-spin" />}
            {status === "success" && <Check size={16} />}
            {status === "idle" && <Send size={16} />}
            {status === "error" && <Send size={16} />}
            <span>
              {status === "success" ? "Listo!" : status === "loading" ? "" : "Suscribirme"}
            </span>
          </button>
        </motion.form>

        {message && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 text-sm ${
              status === "success" ? "text-[#D4AF37]" : "text-[#E07A5F]"
            }`}
            data-testid="newsletter-message"
          >
            {message}
          </motion.p>
        )}
      </div>
    </section>
  );
}
